<script setup>
import { ref, computed, inject, reactive, watch, onMounted } from 'vue'
import BcsWizardSteps from '../../common/BcsWizardSteps.vue'
import Step1Reminders from './Step1Reminders.vue'
import Step2Recipients from './Step2Recipients.vue'
import Step3Summary from './Step3Summary.vue'
import BcsDynamicText from '../../BcsDynamicText.vue'
import { subtractBusinessDays } from '../../Reminders/dateUtils.js'
import { formatMMMDYYYY, parseContactEmails } from '../helpers'
import { ActionCenterRemindersService } from '../../../services/ActionCenterRemindersService'
import { useActionCenterStore } from '../../../stores/ActionCenterStore'
import { useTemplateVariablesStore } from '../../../stores/TemplateVariablesStore'
import { handleErrors } from '../../../utils/errorHandler'

const props = defineProps({ 
  requirements: { type: Array, required: true },
  wasNext30DaysFilter: { type: Boolean, default: false }
})
const emit = defineEmits(['back'])
const emitter = inject('emitter', null)
const functionsBaseUrl = inject('functionsBaseUrl')

// Initialize reminders service
const remindersService = ActionCenterRemindersService(functionsBaseUrl)

// Action Center store for sharing last reminders result
const actionCenterStore = useActionCenterStore()

// Template variables store (used to resolve eventBody ids to real text)
const templateStore = useTemplateVariablesStore()
const { getDefinitionById } = templateStore

// State
const isSending = ref(false)
const sortDirection = ref('asc')

const internalRequirements = ref(props.requirements.map(r => ({
  ...r,
  selected: r.selected !== false,
  expanded: false,
  selectedEmployerKeys: new Set((r.employers || []).map(e => e.plan_year_id))
})))

// Centralized email state per employer (shared across all steps)
// Key: employerId, Value: { originalEmails: string[], selectedEmails: Set<string>, additionalEmails: string[] }
const employerEmailsState = reactive({})

// Initialize email state for ALL employers upfront
function initializeAllEmployerEmails() {
  const seenEmployerIds = new Set()
  for (const r of internalRequirements.value) {
    for (const e of (r.employers || [])) {
      if (seenEmployerIds.has(e.employerId)) continue
      seenEmployerIds.add(e.employerId)
      
      // Only initialize if not already present (preserve user edits)
      if (!employerEmailsState[e.employerId]) {
        const contactEmailsArray = parseContactEmails(e.contactEmails)
        employerEmailsState[e.employerId] = {
          originalEmails: [...contactEmailsArray], // Employer contact emails as checkboxes
          selectedEmails: new Set(contactEmailsArray), // All selected by default
          additionalEmails: [] // Start empty, user adds manually
        }
      }
    }
  }
}

// Initialize employer emails on mount
onMounted(() => {
  initializeAllEmployerEmails()
})

// Re-initialize when employer selection changes in Step 1 (to catch newly added employers)
watch(
  () => {
    const ids = new Set()
    for (const r of internalRequirements.value) {
      for (const e of (r.employers || [])) {
        if (r.selectedEmployerKeys.has(e.plan_year_id)) {
          ids.add(e.employerId)
        }
      }
    }
    return Array.from(ids).sort().join(',')
  },
  () => {
    initializeAllEmployerEmails()
  }
)

const steps = ref([
  { title: 'Confirm & Select Reminders', description: 'step1Subtitle' },
  { title: 'Confirm Employer Recipients', description: 'step2Subtitle' },
  { title: 'Calendar Reminder(s) Summary', description: 'step3Subtitle' }
])
const activeStep = ref(0)
const currentStep = computed(() => steps.value[activeStep.value] || { title: '', description: '' })

// Requirement interactions
function toggleSelectAll(e) {
  const should = e.target.checked
  internalRequirements.value.forEach(r => r.selected = should)
}
function toggleRequirement(req) { req.selected = !req.selected }
function toggleEmployersExpand(req) { req.expanded = !req.expanded }
function toggleSort() {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  internalRequirements.value.sort((a, b) => {
    const aDate = a.earliestDueDateObj ? a.earliestDueDateObj.getTime() : 0
    const bDate = b.earliestDueDateObj ? b.earliestDueDateObj.getTime() : 0
    return sortDirection.value === 'asc' ? aDate - bDate : bDate - aDate
  })
}
function toggleEmployerSelection({ req, emp }) {
  const key = emp.plan_year_id
  const next = new Set(req.selectedEmployerKeys)
  next.has(key) ? next.delete(key) : next.add(key)
  req.selectedEmployerKeys = next
}
function onRequirementClicked(req) {
  const firstEmp = req.employers && req.employers.length ? req.employers[0] : null
  if (!firstEmp || !emitter) return
  emitter.emit('action-center:event-clicked', {
    event: { id: firstEmp.eventId || null, name: req.requirementName, body: req.eventBody, date: req.eventMonthDay, dueDate: req.earliestDueDateObj },
    employer: { name: firstEmp.employerName, plan_start_date: firstEmp.plan_start_date, id: firstEmp.employerId },
    source: 'action-center'
  })
}

function onBusinessDaysChange({ opt, req }) {
  const val = parseInt(opt.value, 10)
  req.reminderBusinessDays = Number.isNaN(val) ? null : val
  
  // Handle custom date selection
  if (opt.isCustom && opt.customDays != null) {
    req.customReminderDays = opt.customDays
    req.customDate = opt.customDate
    req.reminderDateObj = new Date(opt.customDate)
    req.reminderDateStr = req.reminderDateObj ? formatMMMDYYYY(req.reminderDateObj) : '--'
    return
  }
  
  // Handle re-selecting existing custom date option (-2) from dropdown
  if (val === -2 && req.customReminderDays != null && req.customDate) {
    req.reminderDateObj = new Date(req.customDate)
    req.reminderDateStr = req.reminderDateObj ? formatMMMDYYYY(req.reminderDateObj) : '--'
    return
  }
  
  // Clear custom date fields for non-custom selections
  req.customReminderDays = null
  req.customDate = null
  
  if (req.reminderBusinessDays === 0) {
    const t = new Date()
    req.reminderDateObj = new Date(t.getFullYear(), t.getMonth(), t.getDate())
  } else if (req.reminderBusinessDays > 0 && req.earliestDueDateObj) {
    req.reminderDateObj = subtractBusinessDays(req.earliestDueDateObj, req.reminderBusinessDays)
  } else {
    req.reminderDateObj = null
  }
  req.reminderDateStr = req.reminderDateObj ? formatMMMDYYYY(req.reminderDateObj) : '--'
}

// Email state handlers (to be called from Step2Recipients)
function updateEmployerEmails(employerId, { selectedEmails, additionalEmails }) {
  if (!employerEmailsState[employerId]) return
  employerEmailsState[employerId].selectedEmails = new Set(selectedEmails)
  employerEmailsState[employerId].additionalEmails = [...additionalEmails]
}

// Summary & validation
const selectedSummary = computed(() => {
  const employerIds = new Set()
  let reqs = 0
  for (const r of internalRequirements.value) {
    if (!(r.selected && r.reminderBusinessDays != null && r.selectedEmployerKeys.size > 0)) continue
    reqs++
    for (const e of (r.employers || [])) {
      if (r.selectedEmployerKeys.has(e.plan_year_id)) employerIds.add(e.employerId)
    }
  }
  return { totalEmployers: employerIds.size, totalRequirements: reqs, show: employerIds.size > 0 && reqs > 0 }
})

const allSelected = computed(() => internalRequirements.value.length > 0 && internalRequirements.value.every(r => r.selected))

const allSelectedRequirementsValid = computed(() => {
  const selected = internalRequirements.value.filter(r => r.selected)
  return selected.length > 0 && selected.every(r => r.reminderBusinessDays != null && r.selectedEmployerKeys.size > 0)
})

const nextButtonLabel = computed(() => {
  if (activeStep.value === 0) return 'Step 2: Choose Recipients'
  if (activeStep.value === 1) return 'Step 3: Preview & Confirm'
  if (activeStep.value === 2) return 'Send Reminders'
  return 'Continue'
})

// Build final payload including selected emails per employer (sync; used for UI/preview)
function buildSelectedPayloadPreview() {
  const selected = internalRequirements.value
    .filter(req => req.selected && req.reminderBusinessDays != null && req.selectedEmployerKeys.size > 0)
    .map(req => {
      const filteredEmployers = (req.employers || [])
        .filter(emp => req.selectedEmployerKeys.has(emp.plan_year_id))
        .map(emp => {
          // Get selected emails for this employer from centralized state
          const emailState = employerEmailsState[emp.employerId]          
          // selectedEmails includes:checked agency emails + all Additional emails
          const selectedEmailsList = emailState
            ? [
                ...emailState.originalEmails.filter(email => emailState.selectedEmails.has(email)),
                ...emailState.additionalEmails
              ]
            : []
          
          return {
            ...emp,
            selectedEmails: selectedEmailsList
          }
        })
      
      return {
        key: req.key,
        requirementName: req.requirementName,
        eventBody: req.eventBody,
        eventMonthDay: req.eventMonthDay,
        earliestDueDateObj: req.earliestDueDateObj,
        reminderBusinessDays: req.reminderBusinessDays,
        customReminderDays: req.customReminderDays || null,
        customDate: req.customDate || null,
        reminderDateObj: req.reminderDateObj,
        reminderDateStr: req.reminderDateStr,
        employers: filteredEmployers
      }
    })
  return { items: selected, step: activeStep.value }
}

// Build final payload for API (resolves template ids to body text)
async function buildSelectedPayload() {
  const payload = buildSelectedPayloadPreview()

  const resolvedItems = await Promise.all(
    (payload.items || []).map(async (item) => {
      const raw = item?.eventBody || ''
      if (!raw) return item

      const def = await getDefinitionById(raw, item?.requirementName || '')
      return {
        ...item,
        eventBody: def?.body ?? raw
      }
    })
  )

  return {
    ...payload,
    items: resolvedItems
  }
}

function goToNextStep() {
  if (activeStep.value < steps.value.length - 1) {
    activeStep.value++
    return
  }
  sendReminders()
}
function goToPrevStep() {
  if (activeStep.value > 0) activeStep.value--
}

// Step 3 handlers
function goToStep(step) {
  activeStep.value = step
}

function buildOptimisticResult(apiPayload) {
  const reminders = apiPayload.reminders || []
  const details = reminders.map(r => ({
    success: true,
    requirementKey: r.requirementKey,
    employerId: r.employerId,
    employerName: r.employerName,
    attendeesCount: r.attendees.length,
    name: r.requirementName,
    due_date: r.dueDate,
    start_date: r.reminderDate,
    created_at: new Date().toISOString(),
    target_emails: r.attendees.map(a => a.email).join(', ')
  }))
  return {
    success: true,
    job_id: null,
    total: reminders.length,
    completed: reminders.length,
    failed: 0,
    duration_ms: 0,
    details
  }
}

async function sendReminders() {
  if (isSending.value) return

  isSending.value = true

  try {
    const payload = await buildSelectedPayload()

    const apiPayload = remindersService.preparePayloadForApi(payload)

    // Show optimistic result immediately so the banner appears without delay
    actionCenterStore.setLastRemindersSummary(buildOptimisticResult(apiPayload), props.wasNext30DaysFilter)

    remindersService.sendBulkReminders(apiPayload)
      .then((result) => {
        actionCenterStore.loadHistoricalReminders()
      })
      .catch((error) => {
        handleErrors('sendBulkReminders', error)
      })

    // Clear saved selections so they are not restored when returning to dashboard
    actionCenterStore.savedDeadlinesSelection = null

    // Return to dashboard
    emit('back')
  } catch (error) {
    handleErrors('sendBulkReminders', error)
    emit('back')
  } finally {
    isSending.value = false
  }
}

// Step 3 expand/collapse state
const step3AllExpanded = ref(false)
const step3ExpandAll = ref(null) // null = no change, true = expand all, false = collapse all

function toggleAllStep3Sections() {
  step3ExpandAll.value = !step3AllExpanded.value
}

function onStep3ExpandedStateChanged(allExpanded) {
  step3AllExpanded.value = allExpanded
  step3ExpandAll.value = null // Reset trigger
}

// Step 2 employer aggregation
const employersForStep2 = computed(() => {
  const map = new Map()
  for (const r of internalRequirements.value) {
    if (!(r.selected && r.reminderBusinessDays != null && r.selectedEmployerKeys.size > 0)) continue
    for (const e of (r.employers || [])) {
      if (!r.selectedEmployerKeys.has(e.plan_year_id)) continue
      if (!map.has(e.employerId)) {
        map.set(e.employerId, {
          employerId: e.employerId,
          employerName: e.employerName,
          contactEmails: e.contactEmails || '',
          planYearIds: new Set([e.plan_year_id])
        })
      } else {
        map.get(e.employerId).planYearIds.add(e.plan_year_id)
      }
    }
  }
  return Array.from(map.values()).map(row => ({
    employerId: row.employerId,
    employerName: row.employerName,
    contactEmails: row.contactEmails,
    setsCount: row.planYearIds.size
  })).sort((a, b) => a.employerName.localeCompare(b.employerName))
})

// Check if all employers have at least one selected or additional email (required for step 2)
const allEmployersHaveAdditionalEmail = computed(() => {
  return employersForStep2.value.every(emp => {
    const emailState = employerEmailsState[emp.employerId]
    if (!emailState) return false
    return emailState.selectedEmails.size > 0 || emailState.additionalEmails.length > 0
  })
})

const canProceed = computed(() => {
  if (activeStep.value === 0) return allSelectedRequirementsValid.value
  if (activeStep.value === 1) return allEmployersHaveAdditionalEmail.value
  if (activeStep.value === 2) return allSelectedRequirementsValid.value
  return false
})

const wizardSteps = computed(() => {
  return steps.value.map((step, index) => {
    let status
    if (index < activeStep.value) {
      status = 'completed'
    } else if (index > activeStep.value) {
      status = 'empty'
    } else {
      status = canProceed.value ? 'completed' : 'active'
    }
    return { ...step, status }
  })
})

// Data for Step 3: includes requirements with employers and their selected emails
const dataForStep3 = computed(() => buildSelectedPayloadPreview())
</script>

<template>
  <div class="send-reminders-container bcs-page-padding">
    <div class="mb-3 mt-4.5">
      <button class="btn btn-link text-decoration-none px-0 text-primary text-14" @click="$emit('back')">
        <i class="bi bi-chevron-left"></i><strong> Back to Main Action Center</strong>
      </button>
    </div>
    <h2 class="text-28 mb-4.5"><med>Send Requirement Reminders</med></h2>
    <div class="mb-3">
      <BcsWizardSteps :steps="wizardSteps" :activeStep="activeStep" :wrapLabels="true" />
    </div>

    <!-- Step title and description -->
    <div class="d-flex justify-content-between align-items-start my-4">
      <div class="step-header-content">
        <h3 class="header-20 mb-2"><med>Step {{ activeStep + 1 }}: {{ currentStep.title }}</med></h3>
        <p class="text-16 mb-1"><BcsDynamicText :text="currentStep.description" /></p>
      </div>
      <button 
        v-if="activeStep === 2"
        type="button" 
        class="btn btn-link text-decoration-underline p-0 text-16 text-nowrap ms-3 align-self-end"
        @click="toggleAllStep3Sections"
      >
        {{ step3AllExpanded ? 'Collapse All Fields' : 'Expand All Fields' }}
      </button>
    </div>

    <Step1Reminders 
      v-if="activeStep === 0"
      :internal-requirements="internalRequirements"
      :all-selected="allSelected"
      :selected-summary="selectedSummary"
      :can-show-summary="true"
      :sort-direction="sortDirection"
      @toggle-select-all="toggleSelectAll"
      @toggle-requirement="toggleRequirement"
      @toggle-employers-expand="toggleEmployersExpand"
      @toggle-employer-selection="toggleEmployerSelection"
      @requirement-clicked="onRequirementClicked"
      @business-days-change="onBusinessDaysChange"
      @toggle-sort="toggleSort"
    />
    <Step2Recipients 
      v-else-if="activeStep === 1" 
      :employers-for-step2="employersForStep2"
      :employer-emails-state="employerEmailsState"
      @update-employer-emails="updateEmployerEmails"
    />
    <Step3Summary 
      v-else-if="activeStep === 2" 
      :current-step="currentStep"
      :reminder-data="dataForStep3"
      :expand-all="step3ExpandAll"
      @go-to-step="goToStep"
      @expanded-state-changed="onStep3ExpandedStateChanged"
    />

    <!-- Footer buttons -->
    <div class="d-flex justify-content-between align-items-center mt-4">
      <button v-if="activeStep > 0" class="btn btn-outline-info btn-lg px-4" :disabled="isSending" @click="goToPrevStep">
        <i class="bi bi-chevron-left me-1"></i>
        <span v-if="activeStep === 1">Back to Step 1: Choose Reminders</span>
        <span v-else-if="activeStep === 2">Back to Step 2: Confirm Recipients</span>
      </button>
      <span v-else></span>
      <div class="d-flex gap-3">
        <button v-if="activeStep === 2" class="btn btn-outline-primary btn-lg d-flex align-items-center px-4" :disabled="isSending" @click="$emit('back')">
          <i class="bi bi-x-circle-fill me-1 d-flex me-2 text-16"></i>Cancel Reminders
        </button>
        <button 
          class="btn btn-primary btn-lg px-4" 
          :disabled="!canProceed || isSending" 
          @click="goToNextStep"
        >
          <span v-if="isSending && activeStep === 2">
            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Sending...
          </span>
          <span v-else>
            {{ nextButtonLabel }}
            <i v-if="activeStep < 2" class="bi bi-arrow-right ms-1"></i>
            <i v-else class="bi bi-send-fill ms-1 text-14"></i>
          </span>
        </button>
      </div>
    </div>

  </div>
</template>

<style scoped>
.send-reminders-container {
  min-height: 68vh;
}

.step-header-content {
  flex: 0 0 71%;
  max-width: 71%;
}
</style>
